import { FC } from 'react';
import {
  ComponentRendering,
  Field,
  LayoutServiceData,
  LinkField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { GetStaticPropsContext } from 'next';

import { icon_arrow_right } from '@gn-audio/icons';
import {
  Button,
  ComponentProps,
  isColorBright,
  JabraIcon,
  resolveColorProperty,
} from '@gn-audio/jss-components';
import {
  FamilyGroup,
  getFamilyGroups,
  getLocaleWithRegion,
  getRelatedProductsByGroup,
  getSharedProperty,
  Group,
} from '@gn-audio/jss-core';

import { Eyebrow } from 'common/components/Eyebrow';
import { parseSCLookupValue } from 'common/siteCoreHelpers';
import { ServiceCard, ServiceCardType } from 'components/Product/ServicesSpot/ServiceCard';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

export type ServicesSpotFields = {
  eyebrowText: Field<string>;
  heading: Field<string>;
  description: Field<string>;
  allServicesLinkLabel: Field<string>;
  allServicesLink: LinkField;
  allServicesLinkAriaLabel?: Field<string>;
  backgroundColor: Field<string>;
  servicesCards: ServiceCardType[];
  trackingSpotName?: Field<string>;
  allServicesLinkTrackingName?: Field<string>;
};

export type MatchedRelatedServices = {
  relatedService: Group;
  serviceCard: ServiceCardType;
};

export type ServicesSpotProps = {
  fields: ServicesSpotFields;
  matchedRelatedServices: MatchedRelatedServices[];
} & ComponentProps<ServicesSpotFields>;

const removeDuplicatesByKey = (array: Group[], field: string): Group[] => {
  const seen = new Map<string | number | boolean, Group>();

  for (const item of array) {
    const value = item[field as keyof Group];

    if (value != null && !seen.has(value)) {
      seen.set(value, item);
    }
  }

  return Array.from(seen.values());
};

const matchRelatedServicesWithSitecoreServices = (
  relatedServices: Group[],
  servicesCards: ServiceCardType[]
): MatchedRelatedServices[] => {
  const serviceCardLookup = new Map(
    servicesCards?.map((card) => [card.serviceName?.value.toLocaleLowerCase() || '', card])
  );

  return relatedServices
    .map((relatedService) => {
      const serviceName = relatedService.name?.toLocaleLowerCase();
      if (!serviceName) return null;

      const matchedCard = [...serviceCardLookup.entries()].find(([key]) =>
        serviceName.includes(key)
      )?.[1];

      return matchedCard ? { relatedService, serviceCard: matchedCard } : null;
    })
    .filter((entry): entry is MatchedRelatedServices => entry !== null);
};

const ServicesSpot: FC<ServicesSpotProps> = ({ fields, matchedRelatedServices }) => {
  const {
    eyebrowText,
    heading,
    description,
    allServicesLinkLabel,
    allServicesLink,
    allServicesLinkAriaLabel,
    backgroundColor,
    servicesCards,
    trackingSpotName,
    allServicesLinkTrackingName,
  } = fields;
  const spotRef = useSpotTracking(trackingSpotName?.value);

  const resolvedBackgroundColor = resolveColorProperty(backgroundColor?.value);
  const isBackgroundBright = isColorBright(resolvedBackgroundColor);
  const textColorClass = isBackgroundBright ? 'text-black' : 'text-white';

  if (matchedRelatedServices?.length === 0) return null;

  const ServiceCards = () => (
    <>
      {matchedRelatedServices.map((service) => {
        return (
          <ServiceCard
            key={service.serviceCard.id}
            serviceCard={service.serviceCard}
            relatedService={service.relatedService}
          />
        );
      })}
    </>
  );

  return (
    <div
      ref={spotRef}
      data-wt-spot={trackingSpotName?.value}
      style={{ backgroundColor: resolvedBackgroundColor }}
    >
      <div className="container">
        <div className={classNames('flex flex-col items-center justify-center', textColorClass)}>
          {!!eyebrowText?.value && <Eyebrow align="center" text={eyebrowText?.value} />}
          {!!heading?.value && (
            <h2 className="py-6 text-center text-4xl lg:text-6xl">{heading?.value}</h2>
          )}
          {!!description?.value && <p className="text-center text-2xl">{description?.value}</p>}
        </div>
        {servicesCards?.length > 0 && (
          <div className="flex flex-col gap-6 py-8 lg:flex-row lg:gap-0 lg:px-16 lg:py-16">
            <ServiceCards />
          </div>
        )}
        {!!allServicesLink?.value?.href && !!allServicesLinkLabel?.value && (
          <div className="flex flex-col items-center justify-center">
            <Button
              className="pt-5"
              size="m"
              variant="primary"
              asLink
              href={allServicesLink.value.href}
              iconPlacement="right"
              icon={<JabraIcon Icon={icon_arrow_right} />}
              {...(allServicesLinkAriaLabel?.value && {
                'aria-label': allServicesLinkAriaLabel.value,
              })}
              {...(allServicesLinkTrackingName?.value && {
                'data-wt-name': allServicesLinkTrackingName.value,
              })}
            >
              {allServicesLinkLabel?.value}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
export const getStaticProps = async (
  rendering: ComponentRendering<ServicesSpotFields>,
  layout: LayoutServiceData,
  context: GetStaticPropsContext
) => {
  try {
    if (!rendering.fields) {
      throw new Error('No fields found for Services Spot');
    }
    const productFamilyId = getSharedProperty(
      layout.sitecore.route?.fields,
      'Product'
    ) as Field<string>;

    const { servicesCards } = rendering.fields;
    const familyLookupCode = parseSCLookupValue(productFamilyId?.value);
    const language = getLocaleWithRegion(context.locale);
    const groups = (await getFamilyGroups(familyLookupCode.id, language)) as FamilyGroup[];

    const services = (await getRelatedProductsByGroup(groups[0].groupId, language)) || [];
    const relatedServices = removeDuplicatesByKey(services, 'pageUrl');

    const matchedRelatedServices = matchRelatedServicesWithSitecoreServices(
      relatedServices,
      servicesCards
    );

    return { matchedRelatedServices };
  } catch (error) {
    console.log(error);
    const { params = {} } = rendering;
    return { relatedServices: [], params };
  }
};
export default withDatasourceCheck()<ServicesSpotProps>(ServicesSpot);
